import logger from '@/utils/logger';
/**
 * OPTIMISATION: Cache intelligent pour profil utilisateur
 * 
 * Contourne le cold start Supabase en cachant localement le profil.
 * SÉCURISÉ: Seules les données non-sensibles sont cachées.
 */

import type { Profile } from '@/lib/supabase';

const CACHE_KEY = 'clairefy_profile_cache';
const CACHE_DURATION = 30 * 60 * 1000; // 30 minutes (au lieu de 5) pour éviter cold starts

interface CachedProfile {
 profile: Profile;
 timestamp: number;
 userId: string;
}

/**
 * SÉCURITÉ: Ne cacher que les données publiques du profil
 * PAS de tokens, PAS de données sensibles
 */
function sanitizeProfileForCache(profile: Profile): Profile {
 return {
 id: profile.id,
 email: profile.email,
 full_name: profile.full_name,
 avatar_url: profile.avatar_url,
 created_at: profile.created_at,
 updated_at: profile.updated_at,
 // Exclure tokens_balance, stripe_customer_id, etc.
 } as Profile;
}

/**
 * Sauvegarder le profil en cache
 */
export function cacheProfile(profile: Profile, userId: string): void {
 try {
 const sanitized = sanitizeProfileForCache(profile);
 const cached: CachedProfile = {
 profile: sanitized,
 timestamp: Date.now(),
 userId
 };
 localStorage.setItem(CACHE_KEY, JSON.stringify(cached));
 } catch (error) {
 logger.warn('Failed to cache profile:', error);
 }
}

/**
 * Récupérer le profil depuis le cache
 * 
 * @returns Profile si valide et récent, null sinon
 */
export function getCachedProfile(userId: string): Profile | null {
 try {
 const cached = localStorage.getItem(CACHE_KEY);
 if (!cached) return null;

 const parsed: CachedProfile = JSON.parse(cached);

 // SÉCURITÉ: Vérifier que le cache correspond au bon utilisateur
 if (parsed.userId !== userId) {
 localStorage.removeItem(CACHE_KEY);
 return null;
 }

 // Vérifier expiration (30 minutes)
 const age = Date.now() - parsed.timestamp;
 if (age > CACHE_DURATION) {
 localStorage.removeItem(CACHE_KEY);
 return null;
 }

 return parsed.profile;
 } catch (error) {
 logger.warn('Failed to get cached profile:', error);
 localStorage.removeItem(CACHE_KEY);
 return null;
 }
}

/**
 * Invalider le cache (déconnexion, mise à jour profil, etc.)
 */
export function invalidateProfileCache(): void {
 localStorage.removeItem(CACHE_KEY);
}

